<template>
  <div>
    <MainLayout>
      <template #header>
        <div class="mt-8 max-w-3xl mx-auto sm:px-6 lg:max-w-7xl flex justify-end">
          <JobActions :record="job" @reload="getReload" />
        </div>
      </template>
      <StaleJobBanner
        v-if="job.isStale"
        :title="bannerTitle"
        :message="bannerMessage"
        container-class="mx-auto max-w-3xl sm:px-6 lg:max-w-7xl"
      >
        <template #action>
          <router-link
            v-if="showdraftJobDetails"
            :to="{ path: `${$route.path}/edit`, query: { draft: true } }"
            class="mt-4 inline-block text-yellow-900 underline hover:no-underline"
          >
            Review your AI-powered job refresh
          </router-link>
          <router-link
            v-else
            :to="{ path: `${$route.path}/edit` }"
            class="mt-4 inline-block text-yellow-900 underline hover:no-underline"
          >
            Edit job details
          </router-link>
        </template>
      </StaleJobBanner>
      <div
        class="mt-8 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3"
      >
        <div class="space-y-6 lg:col-start-1 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>{{ job.title }}</CardTitle>
              <CardAction>
                <span
                  role="checkbox"
                  tabindex="0"
                  aria-checked="false"
                  :class="jobStatus == 'Active' ? 'bg-indigo-600' : 'bg-gray-200'"
                  class="mt-2 inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:shadow-outline"
                  @click="toggleJob()"
                >
                  <span
                    aria-hidden="true"
                    :class="jobStatus == 'Active' ? 'translate-x-5' : 'translate-x-0'"
                    class="translate-x-0 inline-block h-5 w-5 rounded-full bg-white shadow transform transition ease-in-out duration-200"
                  ></span>
                </span>
              </CardAction>
            </CardHeader>
            <CardContent>
              <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
                <div class="sm:col-span-2">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Title</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    {{ job.title }}
                  </dd>
                </div>
                <div class="sm:col-span-2">
                  <dt class="text-sm leading-5 font-medium text-gray-500">Description</dt>
                  <dd class="mt-1 text-sm leading-5 text-gray-900">
                    <div v-html="job.description"></div>
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
        <section aria-labelledby="timeline-title" class="lg:col-start-3 lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle id="timeline-title">Cities</CardTitle>
            </CardHeader>
            <CardContent v-if="!job.cities?.length">No cities yet</CardContent>
            <CardContent class="flex flex-col gap-y-2">
              <Badge
                v-for="location in job.cities"
                :key="location.id"
                variant="blue"
                class="cursor-pointer"
                @click="handleCityClick(location)"
                @mouseover="handleCityMouseOver(location)"
                @mouseleave="handleCityMouseLeave"
              >
                <div class="flex items-center gap-x-1">
                  <span>{{ formatCityState(location) }}</span>
                  <span
                    v-if="showEditCity && cityBeingEdited?.id === location.id && job.cities.length > 1"
                    class="flex items-center"
                  >
                    <vue-feather type="x" class="h-4 align-middle" />
                  </span>
                </div>
              </Badge>
            </CardContent>
            <CardContent>
              <Badge variant="primary" class="cursor-pointer" @click="showJobCityNew = true">
                <vue-feather type="plus" class="h-4" />
              </Badge>
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle id="timeline-title"> Candidates {{ job.resumeRequired ? '*' : '' }} </CardTitle>
            </CardHeader>
            <CardContent v-if="job.resumeRequired"> * Resumes are required to apply for this position </CardContent>
            <CardContent v-if="job.contacts.length == 0"> No candidates yet </CardContent>
            <CardContent>
              <ul role="list" class="-mb-8">
                <li v-for="contact of job.contacts" :key="contact.id">
                  <div class="relative pb-8">
                    <div class="relative flex space-x-3">
                      <div>
                        <span
                          class="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white"
                        >
                          <!-- Heroicon name: solid/user -->
                          <svg
                            class="w-5 h-5 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                              clip-rule="evenodd"
                            />
                          </svg>
                        </span>
                      </div>
                      <div class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4">
                        <div>
                          <p class="text-sm text-gray-500">
                            <a
                              :href="`/contacts/${contact.id}`"
                              class="font-medium text-indigo-600 hover:text-indigo-500"
                              target="_blank"
                              >{{ contact.first_name }} {{ contact.last_name }}
                            </a>
                          </p>
                        </div>
                        <div class="text-right text-sm whitespace-nowrap text-gray-500">
                          {{ contact.source?.split('-')[1] }}
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              </ul>
            </CardContent>
          </Card>
        </section>
      </div>

      <div
        class="mt-6 max-w-3xl mx-auto grid grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-1"
      >
        <JobHistory :job="job" />
      </div>

      <JobCityNew
        v-if="showJobCityNew"
        :record="job"
        :initial-cities="job.cities"
        @handle-job-city-new-create="handleJobCityNewCreate"
        @handle-job-city-new-cancel="handleJobCityNewCancel"
      />
      <JobCityDelete
        v-if="showJobCityDelete"
        :v2-job-id="job.id"
        :job-location-id="selectedLocation?.id"
        @handle-job-city-delete="handleJobCityDelete"
        @handle-job-city-delete-cancel="handleJobCityDeleteCancel"
      />
    </MainLayout>
  </div>
</template>

<script>
import { useJobStore } from '../../stores/job.ts';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import JobActions from './JobActions.vue';
import JobHistory from './JobHistory.vue';
import JobCityNew from './JobCityNew.vue';
import JobCityDelete from './JobCityDelete.vue';
import RailsUtility from '../../utilities/rails.ts';
import StaleJobBanner from './StaleJobBanner.vue';
import Badge from '../ui/badge/Badge.vue';
import { toast } from 'vue-sonner';

export default {
  components: {
    MainLayout,
    Card,
    CardAction,
    CardContent,
    CardHeader,
    CardTitle,
    JobActions,
    JobHistory,
    JobCityNew,
    JobCityDelete,
    StaleJobBanner,
    Badge,
  },
  setup() {
    const jobStore = useJobStore();
    return {
      jobStore,
    };
  },
  data() {
    return {
      jobId: null,
      jobStatus: '',
      showEditCity: false,
      cityBeingEdited: null,
      cityBeingDeleted: '',
      showJobCityNew: false,
      showJobCityDelete: false,
      selectedLocation: null,
    };
  },
  computed: {
    job() {
      return this.jobStore.currentJob;
    },
    showdraftJobDetails() {
      return this.job.draftJobDetails;
    },
    bannerTitle() {
      return this.job.draftJobDetails ? 'Review your AI-generated job refresh' : undefined;
    },
    bannerMessage() {
      return this.job.draftJobDetails
        ? "We've used AI to generate a refreshed version of your job posting. Please review the changes and make any necessary adjustments before publishing."
        : undefined;
    },
  },
  watch: {
    $route: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue?.params?.id != oldValue?.params?.id && this.jobStore) {
          this.jobStore.flushCurrentJob();
        }
      },
    },
  },
  async mounted() {
    this.jobId = parseInt(this.$route.params.id);
    await this.getJob();
  },
  methods: {
    async getReload() {
      await this.getJob();
    },
    async toggleJob() {
      const newStatus = this.jobStatus === 'Active' ? 'Inactive' : 'Active';
      this.jobStatus = newStatus;
      await this.jobStore.updateJob({
        id: parseInt(this.$route.params.id),
        updatedJobAttributes: {
          status: RailsUtility.convertStatusToApi(newStatus),
        },
      });
      toast.success('Job status toggled', {
        action: { label: 'Dismiss' },
        description: "Your job's status was successfully toggled. Use the button below to dismiss this alert.",
      });
    },
    async getJob() {
      await this.jobStore.loadCurrentJob({
        id: parseInt(this.jobId || this.$route.params.id),
      });
      this.jobStatus = this.job.status;
    },
    handleJobCityNewCreate() {
      this.showJobCityNew = false;
      this.getJob();
    },
    handleJobCityNewCancel() {
      this.showJobCityNew = false;
    },
    handleJobCityDelete() {
      this.showJobCityDelete = false;
      this.selectedLocation = null;
      this.getJob();
    },
    handleJobCityDeleteCancel() {
      this.showJobCityDelete = false;
      this.selectedLocation = null;
    },
    formatCityState(location) {
      return `${location.city}, ${location.state}`;
    },
    handleCityClick(location) {
      if (this.job.cities.length > 1) {
        this.selectedLocation = location;
        this.showJobCityDelete = true;
      }
    },
    handleCityMouseOver(location) {
      this.showEditCity = true;
      this.cityBeingEdited = location;
    },
    handleCityMouseLeave() {
      this.showEditCity = false;
      this.cityBeingEdited = null;
    },
  },
};
</script>
